import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";
import DashboardLayout from "./layout/DashboardLayout";
import HermesLayout       from "./layout/HermesLayout";
import HermesStatusBadge  from "./components/HermesStatusBadge";

import LoginPage                  from "./pages/LoginPage";
import HubPage                    from "./pages/HubPage";
import TrendRadarPage             from "./pages/TrendRadarPage";
import InstitutionalLearningsPage from "./pages/InstitutionalLearningsPage";
import SourceMonitorPage          from "./pages/SourceMonitorPage";
import DecisionsPage              from "./pages/DecisionsPage";
import VaultPage                  from "./pages/VaultPage";
import EntitiesPage               from "./pages/EntitiesPage";
import SuggestionsPage            from "./pages/SuggestionsPage";
import HermesChatPage             from "./pages/hermes/HermesChatPage";
import IdentityPage               from "./pages/hermes/IdentityPage";
import OperatorPage               from "./pages/hermes/OperatorPage";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-xs text-slate-400 animate-pulse">Loading…</div>
    </div>
  );
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return children;
}

function I({ children }) {
  return (
    <Protected>
      <DashboardLayout>{children}</DashboardLayout>
    </Protected>
  );
}

function H({ children }) {
  return (
    <Protected>
      <HermesLayout>{children}</HermesLayout>
    </Protected>
  );
}

function KnowledgePlaceholder() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="text-[32px] mb-3">📚</div>
        <h1 className="font-display text-[22px] font-bold text-slate-900 mb-1">Knowledge Base</h1>
        <p className="text-[14px] text-slate-500 max-w-sm mx-auto">
          SilverBullet integration coming soon. Your KB has {" "}
          <span className="font-mono font-semibold text-slate-700">111 chunks</span> indexed and searchable by Hermes.
        </p>
        <a href="/" className="mt-6 inline-block text-[13px] text-indigo-600 hover:text-indigo-800 font-medium">← Back to Hub</a>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />

      {/* Hub — top-level platform landing */}
      <Route path="/" element={<Protected><HubPage /></Protected>} />

      {/* Insights module (dashboard lives here) */}
      <Route path="/insights"              element={<I><TrendRadarPage /></I>} />
      <Route path="/insights/suggestions"  element={<I><SuggestionsPage /></I>} />
      <Route path="/insights/entities"     element={<I><EntitiesPage /></I>} />
      <Route path="/insights/sources"      element={<I><SourceMonitorPage /></I>} />
      <Route path="/insights/vault"        element={<I><VaultPage /></I>} />
      <Route path="/insights/learnings"    element={<I><InstitutionalLearningsPage /></I>} />
      <Route path="/insights/decisions"    element={<I><DecisionsPage /></I>} />

      {/* Hermes Control Center */}
      <Route path="/hermes/chat"           element={<H><HermesChatPage /></H>} />
      <Route path="/hermes/identity"       element={<H><IdentityPage /></H>} />
      <Route path="/hermes/operator"       element={<H><OperatorPage /></H>} />
      <Route path="/hermes"               element={<Navigate to="/hermes/chat" replace />} />

      {/* Knowledge Base */}
      <Route path="/knowledge" element={<Protected><KnowledgePlaceholder /></Protected>} />

      {/* Legacy redirects */}
      <Route path="/opportunities"  element={<Navigate to="/insights" replace />} />
      <Route path="/suggestions"    element={<Navigate to="/insights/suggestions" replace />} />
      <Route path="/entities"       element={<Navigate to="/insights/entities" replace />} />
      <Route path="/inbox"          element={<Navigate to="/insights" replace />} />
      <Route path="/sources"        element={<Navigate to="/insights/sources" replace />} />
      <Route path="/vault"          element={<Navigate to="/insights/vault" replace />} />
      <Route path="/learnings"      element={<Navigate to="/insights/learnings" replace />} />
      <Route path="/decisions"      element={<Navigate to="/insights/decisions" replace />} />
      <Route path="/insight/:id"    element={<Navigate to="/insights" replace />} />

      <Route path="*"               element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppShell() {
  const { user } = useAuth();
  return (
    <>
      <AppRoutes />
      <Toaster position="top-right" richColors closeButton />
      {user && <HermesStatusBadge />}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
